import React, { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XCircleIcon } from "@heroicons/react/outline";
import { motion } from "framer-motion";

const getAnimationVariants = () => {
  return window.innerWidth < 640 // sm breakpoint
    ? {
        hidden: { opacity: 0, y: 50 }, // Slide up from bottom
        visible: { opacity: 1, y: 0, transition: { duration: 1 } },
        exit: { opacity: 0, y: 50, transition: { duration: 0.7 } },
      }
    : {
        hidden: { opacity: 0, rotateY: 90 }, // Flip effect
        visible: { opacity: 1, rotateY: 0, transition: { duration: 0.5 } },
        exit: { opacity: 0, rotateY: 90, transition: { duration: 0.5 } },
      };
};

const ModalComponent = ({ onClose, show, title, classes, children }) => {
  const flipVariants = getAnimationVariants(); // Get animation based on screen size

  // Use a class instead of inline style to prevent scrolling when modal is open
  useEffect(() => {
    if (show) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [show]);

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50" onClose={onClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-[2px]" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <motion.div
              variants={flipVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`${classes ? classes : "w-full"} max-w-lg lg:max-w-2xl xl:max-w-4xl 
                          mx-auto transform 
                          bg-gray-50 p-6 shadow-xl 
                          rounded-2xl relative`}
            >
              <Dialog.Panel>
                <Dialog.Title className="text-lg flex justify-between font-medium leading-6 text-gray-900">
                  <span className="text-primary text-xl">{title}</span>
                  <XCircleIcon onClick={onClose} className="w-8 h-8 cursor-pointer bg-gray-50" />
                </Dialog.Title>
                {children}
              </Dialog.Panel>
            </motion.div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalComponent;